import {
  Alert,
  Input,
  InputNumber,
  message,
  Select,
  Spin,
  Switch,
  Tag,
} from 'antd';
import { Check, Edit3, RefreshCcw, Route, Save } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { AppConfigPublishDialog } from '@/components/common/AppConfigPublishDialog';
import { AppConfigVersionPanel } from '@/components/common/AppConfigVersionPanel';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Button } from '@/components/ui/button';
import { authStore, llmRouteConfigStore } from '@/stores';
import type { GatewayRouteView } from '@/types';

interface RouteRow extends GatewayRouteView {
  id: string;
  publicModelID: string;
}

export const LLMRoutesPage = observer(() => {
  const canRead = authStore.can(ADMIN_PERMISSIONS.llmRoutesRead);
  const canWrite = authStore.can(ADMIN_PERMISSIONS.llmRoutesWrite);
  const [publishOpen, setPublishOpen] = useState(false);
  const [editingRowID, setEditingRowID] = useState<string | null>(null);
  const [modelFilter, setModelFilter] = useState<string>();
  const [providerFilter, setProviderFilter] = useState<string>();
  const deploymentPending = llmRouteConfigStore.deploymentPending;

  useEffect(() => {
    if (canRead) void llmRouteConfigStore.load();
  }, [canRead]);
  useEffect(() => {
    if (!deploymentPending) return;
    let stopped = false;
    let timer = 0;
    const poll = async () => {
      try {
        await llmRouteConfigStore.load();
      } finally {
        if (!stopped && llmRouteConfigStore.deploymentPending) {
          timer = window.setTimeout(() => void poll(), 3000);
        }
      }
    };
    timer = window.setTimeout(() => void poll(), 3000);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [deploymentPending]);

  const rows: RouteRow[] = (llmRouteConfigStore.gateway?.models || []).flatMap(
    (model) =>
      model.routes.map((route) => ({
        ...route,
        id: `${model.public_model_id}:${route.route_id}`,
        publicModelID: model.public_model_id,
      })),
  );

  const modelOptions = Array.from(new Set(rows.map((row) => row.publicModelID)))
    .sort()
    .map((value) => ({ label: value, value }));
  const providerOptions = Array.from(
    new Set(
      rows
        .map((row) => row.provider)
        .filter((provider): provider is string => Boolean(provider)),
    ),
  )
    .sort()
    .map((value) => ({ label: value, value }));
  const filteredRows = rows.filter(
    (row) =>
      row.id === editingRowID ||
      ((!modelFilter || row.publicModelID === modelFilter) &&
        (!providerFilter || row.provider === providerFilter)),
  );

  const update = (
    row: RouteRow,
    field: 'priority' | 'weight' | 'enabled' | 'provider' | 'provider_model_id',
    value: number | boolean | string | null,
  ) => {
    if (!row.editable || !row.edit_pointer || value == null) return;
    llmRouteConfigStore.updateRoute(
      row.publicModelID,
      row.route_id,
      row.edit_pointer,
      field,
      value,
    );
  };

  const columns: Column<RouteRow>[] = [
    {
      id: 'model',
      header: '公共模型',
      accessor: (row) => (
        <span className="font-medium">{row.publicModelID}</span>
      ),
    },
    {
      id: 'route',
      header: 'Route ID',
      accessor: (row) => (
        <span className="font-mono text-xs">{row.route_id}</span>
      ),
    },
    {
      id: 'actions',
      header: '操作',
      accessor: (row) => {
        if (!canWrite || !row.editable) return null;
        const editing = editingRowID === row.id;
        return (
          <Button
            variant="ghost"
            size="sm"
            aria-label={`${editing ? '完成编辑' : '编辑'} ${row.route_id}`}
            onClick={() => setEditingRowID(editing ? null : row.id)}
          >
            {editing ? (
              <Check className="mr-1 h-3.5 w-3.5" />
            ) : (
              <Edit3 className="mr-1 h-3.5 w-3.5" />
            )}
            {editing ? '完成' : '编辑'}
          </Button>
        );
      },
    },
    {
      id: 'provider',
      header: 'Provider',
      accessor: (row) =>
        row.editable && canWrite && editingRowID === row.id ? (
          <Input
            value={row.provider || ''}
            onChange={(event) => update(row, 'provider', event.target.value)}
          />
        ) : (
          row.provider || '-'
        ),
    },
    {
      id: 'provider_path',
      header: 'Provider Path',
      accessor: (row) => row.provider_path || '-',
    },
    {
      id: 'upstream',
      header: '上游模型',
      accessor: (row) =>
        row.editable && canWrite && editingRowID === row.id ? (
          <Input
            className="font-mono"
            value={row.provider_model_id || ''}
            onChange={(event) =>
              update(row, 'provider_model_id', event.target.value)
            }
          />
        ) : (
          <span className="font-mono text-xs">
            {row.provider_model_id || '-'}
          </span>
        ),
    },
    {
      id: 'credential',
      header: 'Credential Ref',
      accessor: (row) => (
        <span className="font-mono text-xs">{row.credential_ref || '-'}</span>
      ),
    },
    {
      id: 'supports',
      header: 'Supports',
      accessor: (row) => {
        const values = Object.entries(row.supports || {})
          .filter(([, enabled]) => enabled)
          .map(([name]) => name);
        return values.length > 0 ? values.join(', ') : '-';
      },
    },
    {
      id: 'priority',
      header: 'Route 优先级',
      accessor: (row) =>
        row.editable && canWrite && editingRowID === row.id ? (
          <InputNumber
            min={1}
            value={row.priority}
            onChange={(value) => update(row, 'priority', value)}
          />
        ) : (
          row.priority
        ),
    },
    {
      id: 'weight',
      header: '同级权重',
      accessor: (row) =>
        row.editable && canWrite && editingRowID === row.id ? (
          <InputNumber
            min={1}
            value={row.weight}
            onChange={(value) => update(row, 'weight', value)}
          />
        ) : (
          row.weight
        ),
    },
    {
      id: 'enabled',
      header: '启用',
      accessor: (row) =>
        row.editable && canWrite && editingRowID === row.id ? (
          <Switch
            checked={row.enabled}
            onChange={(value) => update(row, 'enabled', value)}
          />
        ) : row.enabled ? (
          <Tag color="green">启用</Tag>
        ) : (
          <Tag>停用</Tag>
        ),
    },
    {
      id: 'source',
      header: '来源',
      accessor: (row) => (
        <Tag color={row.editable ? 'blue' : undefined}>
          {row.editable ? 'AppConfig' : 'Builtin（只读）'}
        </Tag>
      ),
    },
    {
      id: 'selectable',
      header: '运行时可选',
      accessor: (row) =>
        row.selectable ? (
          <Tag color="green">可选</Tag>
        ) : (
          <Tag color="orange">{row.non_selectable_reason || '不可选'}</Tag>
        ),
    },
  ];

  const preparePublish = async () => {
    if (await llmRouteConfigStore.validate()) setPublishOpen(true);
  };

  const confirmPublish = async () => {
    if (await llmRouteConfigStore.publish()) {
      setEditingRowID(null);
      setPublishOpen(false);
      message.success('LLM 路由配置已发布');
    }
  };

  if (!canRead) {
    return <Alert type="warning" showIcon message="没有读取 LLM 路由的权限" />;
  }

  return (
    <div className="rounded-lg border border-border bg-white shadow-sm">
      <div className="space-y-5 p-6">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-lg font-semibold">
              <Route className="h-5 w-5" /> LLM 路由管理
            </h1>
            <div className="mt-1 text-xs text-muted-foreground">
              数字越小优先级越高；只有同优先级 Route 才按权重选择
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={llmRouteConfigStore.loading}
              onClick={() => {
                setEditingRowID(null);
                void llmRouteConfigStore.load();
              }}
            >
              <RefreshCcw className="mr-2 h-4 w-4" /> 刷新
            </Button>
            {canWrite ? (
              <Button
                disabled={
                  !llmRouteConfigStore.gateway ||
                  llmRouteConfigStore.validating ||
                  deploymentPending ||
                  llmRouteConfigStore.baseVersion <= 0
                }
                onClick={() => void preparePublish()}
              >
                <Save className="mr-2 h-4 w-4" /> 校验并发布
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="公共模型"
            style={{ width: 220 }}
            value={modelFilter}
            options={modelOptions}
            onChange={(value) => {
              setEditingRowID(null);
              setModelFilter(value);
            }}
          />
          <Select
            showSearch
            allowClear
            optionFilterProp="label"
            placeholder="Provider"
            style={{ width: 200 }}
            value={providerFilter}
            options={providerOptions}
            onChange={(value) => {
              setEditingRowID(null);
              setProviderFilter(value);
            }}
          />
          <span className="text-xs text-muted-foreground">
            显示 {filteredRows.length} / {rows.length} 条 Route
          </span>
        </div>

        {llmRouteConfigStore.warning ? (
          <Alert
            type="warning"
            showIcon
            message="Owner Validator 暂不可用，已禁用发布"
            description={llmRouteConfigStore.warning}
          />
        ) : null}
        {llmRouteConfigStore.error ? (
          <Alert type="error" showIcon message={llmRouteConfigStore.error} />
        ) : null}

        <Spin spinning={llmRouteConfigStore.loading}>
          <DataTable
            columns={columns}
            data={filteredRows}
            rowKey="id"
            emptyMessage="暂无规范化 Route"
          />
        </Spin>
        <div className="text-xs text-muted-foreground">
          Base Version: {llmRouteConfigStore.baseVersion || '-'} · Route:{' '}
          {llmRouteConfigStore.gateway?.route_count || 0} · 可选:{' '}
          {llmRouteConfigStore.gateway?.selectable_route_count || 0}
        </div>
        <AppConfigVersionPanel
          currentVersion={llmRouteConfigStore.currentVersion}
          hostedVersions={llmRouteConfigStore.recentHostedVersions}
          deployments={llmRouteConfigStore.recentDeployments}
          canRollback={canWrite && !deploymentPending}
          rollingBack={llmRouteConfigStore.publishing}
          onRollback={(version) => llmRouteConfigStore.rollback(version)}
        />
      </div>

      <AppConfigPublishDialog
        open={publishOpen}
        title="发布 LLM 路由配置"
        before={llmRouteConfigStore.originalDocument}
        after={llmRouteConfigStore.document}
        validation={llmRouteConfigStore.validation}
        publishing={llmRouteConfigStore.publishing}
        deploymentPending={deploymentPending}
        onCancel={() => setPublishOpen(false)}
        onConfirm={() => void confirmPublish()}
      />
    </div>
  );
});
